import type { TOC } from '@ember/component/template-only';
import { LinkTo } from '@ember/routing';
import type { ComponentLike } from '@glint/template';
import { PhArrowRight } from 'ember-phosphor-icons';
import Card from 'tio-ui/components/card';
import Heading from 'tio-ui/components/heading';
import ProgressBar from 'tio-ui/components/progress-bar';

/**
 * Tile layouts. `standard` and `feature` are the `/tas/dashboard` tiles; `metric`,
 * `progress` and `info` are the Figma Employee Home `Hero Card//Metric`,
 * `Hero Card//Progress` and `Hero Card/Info` cards.
 */
export type HeroStatEmphasis = 'standard' | 'feature' | 'metric' | 'progress' | 'info';

interface HeroStatSignature {
  Args: {
    /** What the number counts. Standard tiles show it as an overline; feature tiles show it under the value. */
    label: string;
    /** Displayed as-is. Pass a `:value` block instead to format it, e.g. superscript cents. */
    value?: string | number;
    /** Overline glyph (standard tiles only). */
    icon?: ComponentLike<{ Element: SVGElement }>;
    /** One-line explanation under the value (standard tiles only). */
    hint?: string;
    /**
     * Layout; defaults to `standard`. `feature` is the wide `/tas/dashboard`
     * headline tile (large value first, then optional `:meter` and `:footer`).
     */
    emphasis?: HeroStatEmphasis;
    /** Muted footnote at the bottom of metric, progress and info tiles. */
    context?: string;
    /** 16px glyph after `@context`. */
    contextIcon?: ComponentLike<{ Element: SVGElement }>;
    /** Progress tiles: amount done, in the same unit as `@progressMax`. */
    progressValue?: number;
    /** Progress tiles: the total; defaults to 100. */
    progressMax?: number;
    /** Info tiles: the featured copy under the label. */
    body?: string;
    /** Info tiles: route for the trailing link; the link renders only with `@linkLabel`. */
    linkRoute?: string;
    /** Info tiles: text of the trailing link to `@linkRoute`. */
    linkLabel?: string;
  };
  Blocks: {
    /** Replaces `@value`. */
    value?: [];
    /** Progress meter under the value (feature tiles only). */
    meter?: [];
    /** Muted footnote row, laid out space-between (feature tiles only). */
    footer?: [];
  };
  Element: HTMLDivElement;
}

interface ContextLineSignature {
  Args: { text?: string; icon?: ComponentLike<{ Element: SVGElement }> };
}

const ContextLine: TOC<ContextLineSignature> = <template>
  {{#if @text}}
    <div class="tio-row-tight tio-text-sm-medium tio-fg-muted">
      <span>{{@text}}</span>
      {{#if @icon}}
        {{#let @icon as |Icon|}}
          <Icon class="tio-icon-sm" />
        {{/let}}
      {{/if}}
    </div>
  {{/if}}
</template>;

/** One stat tile inset in a `DashboardHero`. */
const HeroStat: TOC<HeroStatSignature> = <template>
  {{#if (eq @emphasis "feature")}}
    <Card @radius="nested" @padding="sm" ...attributes>
      <div class="tio-col">
        <div class="tio-col-flush gap-0.5">
          <div class="tio-text-2xl-bold tio-fg-primary">
            {{#if (has-block "value")}}{{yield to="value"}}{{else}}{{@value}}{{/if}}
          </div>
          <p class="tio-text-xs-normal tio-fg-muted">{{@label}}</p>
        </div>
        {{yield to="meter"}}
        {{#if (has-block "footer")}}
          <div class="tio-row-between tio-text-xs-normal tio-fg-muted">
            {{yield to="footer"}}
          </div>
        {{/if}}
      </div>
    </Card>
  {{else if (eq @emphasis "metric")}}
    <Card @radius="nested" @padding="sm" class="h-full" ...attributes>
      <div class="tio-col h-full justify-between gap-6">
        <div class="tio-col gap-4">
          <span class="tio-text-base-medium tio-fg-secondary">{{@label}}</span>
          <span class="tio-text-2xl-semibold tio-fg-primary">
            {{#if (has-block "value")}}{{yield to="value"}}{{else}}{{@value}}{{/if}}
          </span>
        </div>
        <ContextLine @text={{@context}} @icon={{@contextIcon}} />
      </div>
    </Card>
  {{else if (eq @emphasis "progress")}}
    <Card @radius="nested" @padding="sm" class="h-full" ...attributes>
      <div class="tio-col gap-4">
        <span class="tio-text-base-medium tio-fg-secondary">{{@label}}</span>
        <span class="tio-text-2xl-semibold tio-fg-primary">
          {{#if (has-block "value")}}{{yield to="value"}}{{else}}{{@value}}{{/if}}
        </span>
        <ProgressBar
          @value={{@progressValue}}
          @max={{@progressMax}}
          @color="primary"
          @size="sm"
          @label={{@label}}
        />
        <ContextLine @text={{@context}} @icon={{@contextIcon}} />
      </div>
    </Card>
  {{else if (eq @emphasis "info")}}
    <Card @radius="nested" @padding="sm" class="h-full" ...attributes>
      <div class="tio-col h-full justify-center gap-4">
        <div class="tio-col gap-2">
          <span class="tio-text-base-medium tio-fg-secondary">{{@label}}</span>
          <p class="tio-text-base-bold tio-fg-primary">{{@body}}</p>
        </div>
        {{#if @linkRoute}}
          {{#if @linkLabel}}
            <LinkTo @route={{@linkRoute}} class="tio-row-tight tio-text-sm-medium app-text-link">
              {{@linkLabel}}
              <PhArrowRight class="tio-icon-sm" />
            </LinkTo>
          {{/if}}
        {{/if}}
        <ContextLine @text={{@context}} @icon={{@contextIcon}} />
      </div>
    </Card>
  {{else}}
    <Card @radius="nested" @padding="xs" ...attributes>
      <div class="tio-col-flush gap-0.5">
        <div class="tio-text-overline tio-row-snug tio-fg-muted">
          {{#if @icon}}
            {{#let @icon as |Icon|}}
              <Icon class="tio-icon-xs" />
            {{/let}}
          {{/if}}
          {{@label}}
        </div>
        <Heading @level={{3}} @size="lg">
          {{#if (has-block "value")}}{{yield to="value"}}{{else}}{{@value}}{{/if}}
        </Heading>
        {{#if @hint}}
          <span class="tio-text-xs-normal leading-tight tio-fg-muted">{{@hint}}</span>
        {{/if}}
      </div>
    </Card>
  {{/if}}
</template>;

export default HeroStat;
